import { type ColumnDef } from '@tanstack/react-table';
import { format } from 'date-fns';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { SortIcon } from '@/components/common/SortIcon';
import { DeleteDialog } from '@/components/common/Dialogs';
import { getLocalizedName } from '@/lib/utils';
import { useAuthStore } from '@/features/auth/stores/auth.store';
import { rolesQueryKeys } from '../api';
import { RoleStatusBadge } from './RoleStatusBadge';
import { RoleTypeBadge } from './RoleTypeBadge';
import { CreateRoleDialog } from './CreateRoleDialog';
import type { Role, RoleFilters } from '../types';

interface GetColumnsParams {
  orderBy: string;
  order: 'asc' | 'desc' | '';
  onSort: (field: string) => void;
  filters: RoleFilters;
}

export function useRoleColumns({
  orderBy,
  order,
  onSort,
}: GetColumnsParams): ColumnDef<Role>[] {
  const { t, i18n } = useTranslation();
  const hasAccess = useAuthStore((state) => state.hasAccess);

  const columns: ColumnDef<Role>[] = [
    {
      accessorKey: 'name',
      header: () => (
        <div
          className="flex items-center cursor-pointer"
          role="button"
          tabIndex={0}
          onClick={() => onSort('name')}
          onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onSort('name')}
        >
          <p>{t('name')}</p>
          <SortIcon active={orderBy === 'name'} direction={order} />
        </div>
      ),
      cell: ({ row }) => (
        <Link
          to={`/app/roles/${row.original.id}`}
          className="text-blue-700 hover:underline"
        >
          {getLocalizedName(row.original, i18n.language)}
        </Link>
      ),
    },
    {
      accessorKey: 'type',
      header: () => <p>{t('type')}</p>,
      cell: ({ row }) => <RoleTypeBadge type={row.original.type} />,
    },
    {
      accessorKey: 'status',
      header: () => <p>{t('status.status')}</p>,
      cell: ({ row }) => <RoleStatusBadge status={row.original.status} />,
    },
    {
      accessorKey: 'permissions',
      header: () => <p>{t('permissions')}</p>,
      cell: ({ row }) => {
        const permissions = row.original.permissions;
        if (!permissions || permissions.length === 0) {
          return <span className="text-muted-foreground">-</span>;
        }
        return (
          <span className="text-sm">
            {permissions.length} {t('permissions').toLowerCase()}
          </span>
        );
      },
    },
    {
      accessorKey: 'rate',
      header: () => <p>{t('rate')}</p>,
      cell: ({ row }) => {
        const rate = row.original.rate;
        return rate != null ? (
          <span>{rate}</span>
        ) : (
          <span className="text-muted-foreground">-</span>
        );
      },
    },
    {
      accessorKey: 'createdAt',
      header: () => (
        <div
          className="flex items-center cursor-pointer select-none"
          role="button"
          tabIndex={0}
          onClick={() => onSort('createdAt')}
          onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onSort('createdAt')}
        >
          <p>{t('createdAt')}</p>
          <SortIcon active={orderBy === 'createdAt'} direction={order} />
        </div>
      ),
      cell: ({ row }) => {
        const dateStr = row?.original?.createdAt;
        return dateStr ? (
          <div>{format(new Date(dateStr), 'yyyy-MM-dd HH:mm')}</div>
        ) : (
          <span>-</span>
        );
      },
    },
  ];

  // Add actions column if user has permissions
  if (hasAccess('role', 'update') || hasAccess('role', 'delete')) {
    columns.splice(0, 0, {
      id: 'actions',
      header: t('actionsLabel'),
      cell: ({ row }) => {
        const data = row.original;

        return (
          <div className="flex gap-2">
            {hasAccess('role', 'update') && <CreateRoleDialog id={data.id} />}
            {hasAccess('role', 'delete') && (
              <DeleteDialog
                id={data.id}
                title={data.name}
                url="roles"
                queryKey={rolesQueryKeys.lists()}
              />
            )}
          </div>
        );
      },
      enableHiding: false,
    });
  }

  return columns;
}
